import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Input } from '@/components/ui';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import type {
  Service,
  CreateServiceRequest,
  UpdateServiceRequest,
  SubscriptionInterval,
  AssetValidationStatus,
  ThemeLayout,
  FallbackChainEntry,
  FlowType,
} from '@/types/service';
import { EMPTY_ASSET_STATUS } from '@/types/service';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { useMerchants } from '@/hooks/use-merchants';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { useIqServices } from '@/hooks/use-iq-services';
import { useIqServiceInventory } from '@/hooks/use-iq-service-inventory';
import { IQ_SERVICE_KEY_PATTERN } from '@/types/iq-service-inventory';
import { AlertTriangle } from 'lucide-react';
import { FieldHelpPopover } from '@/components/ui/field-help-popover';
// Spec 516: Journey-Host-Picker (publicBaseUrl) — Registry + Stage-Erkennung + Feld-Gate.
import { useAuth } from '@/hooks/use-auth';
import { ROLE_GROUPS } from '@/types/auth';
import { resolvePayBaseUrl } from '@/components/features/payment-link/link-signer/stage-map';
import {
  aliasHostsFor,
  defaultJourneyHost,
  normalizeOrigin,
} from '@/components/features/payment-link/link-signer/alias-hosts';
import { CARRIER_LOGO_OPTIONS } from '@/lib/utils/carrier-logos';
import { PaymentPagePreview } from './payment-page-preview';
import { AssetSlotField } from './asset-slot-field';
import { PreviewDeviceToggle, type DeviceMode } from './preview-device-toggle';
import { AssetStatusIcon } from './asset-status-icon';

const INTERVALS: SubscriptionInterval[] = ['P1D', 'P1W', 'P1M', 'P3M', 'P6M', 'P1Y'];
const BUTTON_TEXT_IDS = ['1', '2', '3', '4'] as const;

// Spec 516: Sentinel fuer "Default (api.<stage>)" im Journey-Host-Picker.
// Kollisionsfrei: echte Optionen sind immer https-Origins.
const JOURNEY_HOST_DEFAULT = 'default';

/**
 * Spec 516 Scope 4 / Trap 2 — EIN Submit-Helfer fuer beide Pfade (Create+Update):
 * Der publicBaseUrl-Key wird NUR aufgenommen, wenn der Nutzer schreiben darf UND
 * sich die Auswahl gegenueber dem geladenen Wert aendert (origin-normalisierter
 * Vergleich). "Default" bei zuvor gesetztem Wert -> `null` (Entfernen); sonst
 * Key weglassen (= unveraendert; verhindert auch den PM-Selbst-403 am BFF-Gate).
 */
function journeyHostSubmitValue(
  loaded: string | null,
  selection: string,
  canWrite: boolean,
): { include: false } | { include: true; value: string | null } {
  if (!canWrite) {
    return { include: false };
  }
  const selected = selection === JOURNEY_HOST_DEFAULT ? null : selection;
  if (selected === loaded) {
    return { include: false };
  }
  return { include: true, value: selected };
}

/**
 * Issue #524 — Theme-Dirty-Guard.
 *
 * Die 9 dem Formular bekannten Theme-Felder als String-Form-State. Der Edit-Submit sendet das
 * `Theme`-Objekt nur, wenn sich mindestens eines dieser Felder gegenueber dem geladenen
 * Ausgangszustand geaendert hat — sonst laesst er `Theme` ganz weg, damit die Facade das
 * bestehende Theme (inkl. formular-unbekannter Felder wie `styleVariables`) nicht voll
 * ersetzt. Muster analog `journeyHostSubmitValue`: der geladene Zustand wird aus dem
 * `service`-Prop abgeleitet, kein separater Snapshot-State.
 */
export interface ThemeFieldValues {
  buttonTextId: string;
  stylesheetUrl: string;
  headerImageUrl: string;
  backgroundImageUrl: string;
  stylesheetAssetId: string;
  headerAssetId: string;
  backgroundAssetId: string;
  benefitText: string;
  carrierLogoId: string;
}

/**
 * Geladene Normalform der Theme-Felder aus dem Service. Einzige Quelle fuer die
 * Reset-Initialisierung (Open-`useEffect`) UND den Dirty-Vergleich am Submit — beide speisen
 * sich hieraus, damit sie nicht auseinanderlaufen (Default-Drift).
 */
export function loadedThemeFieldValues(service: Service | null | undefined): ThemeFieldValues {
  const theme = service?.theme;
  return {
    buttonTextId: String(theme?.buttonTextId ?? 1),
    stylesheetUrl: theme?.stylesheetUrl ?? '',
    headerImageUrl: theme?.headerImageUrl ?? '',
    backgroundImageUrl: theme?.backgroundImageUrl ?? '',
    stylesheetAssetId: theme?.stylesheetAssetId ?? '',
    headerAssetId: theme?.headerAssetId ?? '',
    backgroundAssetId: theme?.backgroundAssetId ?? '',
    benefitText: theme?.benefitText ?? '',
    carrierLogoId: theme?.complianceCarrierLogoId ? String(theme.complianceCarrierLogoId) : '',
  };
}

// Der Select-Sentinel 'none' ("kein Logo") und '' (geladen, kein Logo) sind derselbe Zustand.
function canonicalCarrierLogo(value: string): string {
  return value === 'none' ? '' : value;
}

/** True, wenn sich mindestens eines der 9 Theme-Felder gegenueber `loaded` geaendert hat. */
export function isThemeDirty(loaded: ThemeFieldValues, current: ThemeFieldValues): boolean {
  return (
    loaded.buttonTextId !== current.buttonTextId ||
    loaded.stylesheetUrl !== current.stylesheetUrl ||
    loaded.headerImageUrl !== current.headerImageUrl ||
    loaded.backgroundImageUrl !== current.backgroundImageUrl ||
    loaded.stylesheetAssetId !== current.stylesheetAssetId ||
    loaded.headerAssetId !== current.headerAssetId ||
    loaded.backgroundAssetId !== current.backgroundAssetId ||
    loaded.benefitText !== current.benefitText ||
    canonicalCarrierLogo(loaded.carrierLogoId) !== canonicalCarrierLogo(current.carrierLogoId)
  );
}

// Issue #230: iqServiceId (Credential-Set) ist ein eigenes Feld, nie das Gateway.
const IQ_SERVICE_ID_REGEX = /^iq_[a-z0-9][a-z0-9_]*$/;

interface ServiceFormProps {
  /** Service to edit, or null for create mode */
  service?: Service | null;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Callback when form is submitted for creation */
  onSubmitCreate?: (data: CreateServiceRequest) => void;
  /** Callback when form is submitted for update */
  onSubmitUpdate?: (serviceId: string, data: UpdateServiceRequest) => void;
  /** Whether the submit operation is in progress */
  isSubmitting?: boolean;
}

/**
 * Form dialog for creating or editing a service.
 * Uses controlled inputs with validation.
 */
export function ServiceForm({
  service,
  open,
  onOpenChange,
  onSubmitCreate,
  onSubmitUpdate,
  isSubmitting = false,
}: ServiceFormProps) {
  const isEditMode = !!service;
  const { t } = useTranslation('services');
  // Spec 0220: Merchant-Rollen sind tenant-gebunden — sie waehlen keinen
  // Merchant. Platform sieht/waehlt das Feld, Merchant erbt ownMerchantId.
  const { showMerchant, ownMerchantId } = useMerchantScope();
  // Spec 0220 Fail-safe: Merchant-Rolle ohne Tenant-Bindung darf keinen
  // Service anlegen (statt still mit leerem Merchant zu senden).
  const merchantContextMissing = !isEditMode && !showMerchant && !ownMerchantId;
  // Merchants fuer Combobox im Create-Mode
  const { data: merchantsData, isLoading: merchantsLoading } = useMerchants({ status: 'active', limit: 100 });
  const merchantOptions = (merchantsData?.data ?? []).map((m) => ({
    value: m.merchantId,
    label: m.name,
    description: m.merchantId,
  }));
  // Spec 0216: IQ-Services fuer Primaer-Routing-Combobox im Create-Mode.
  // Im Edit-Mode wird das Routing in der eigenen Routing-View gepflegt — wir
  // ueberspringen den Hook-Call dort um unnoetige Requests zu vermeiden.
  const { data: iqServicesData, isLoading: iqServicesLoading } = useIqServices();
  const iqServiceOptions: ComboboxOption[] = (iqServicesData?.data ?? []).map((svc) => ({
    value: svc.iqServiceId,
    label: svc.displayName,
    description: `${svc.iqServiceId} · ${svc.merchantName} · ${svc.gatewayMode}`,
  }));
  // Form state
  const [merchantId, setMerchantId] = useState('');
  // Spec 0216: Primaer-Routing inline im Create-Mode (1 Eintrag). Edit-Mode
  // erbt das Routing aus dem Service und macht hier NICHTS damit — die
  // Routing-Pflege passiert ausschliesslich in der eigenen Routing-View.
  const [primaryFlowType, setPrimaryFlowType] = useState<FlowType | ''>('');
  const [primaryGatewayServiceId, setPrimaryGatewayServiceId] = useState('');
  // Issue #230: Credential-Set (iq_…) als separates Feld — getrennt vom Gateway.
  const [primaryIqServiceId, setPrimaryIqServiceId] = useState('');
  // UC-5: gatewayServiceId-Dropdown aus dem Merchant-IQ-Service-Inventar (active),
  // gefiltert auf den primaeren FlowType. Distinkt von `useIqServices` (Credential-Sets).
  // Effektiver Merchant: platform waehlt `merchantId`, Merchant-Rollen erben `ownMerchantId`
  // (ihr `merchantId`-State bleibt leer) — sonst bliebe das Dropdown fuer PM leer.
  const effectiveMerchantId = merchantId || (ownMerchantId ?? '');
  const iqInventory = useIqServiceInventory(effectiveMerchantId || undefined, { status: 'active', limit: 200 });
  const primaryGatewayOptions: ComboboxOption[] = (iqInventory.data?.pages.flatMap((p) => p.items) ?? [])
    .filter((item) => !item.flowType || !primaryFlowType || item.flowType === primaryFlowType)
    .map((item) => ({
      value: item.iqServiceKey,
      label: item.displayName ? `${item.displayName} (${item.iqServiceKey})` : item.iqServiceKey,
    }));
  const [description, setDescription] = useState('');
  // Spec 514: admin-seitiges Label (Facade-Service), getrennt von description.
  const [displayName, setDisplayName] = useState('');
  const [grossAmount, setGrossAmount] = useState('');
  const [subscriptionInterval, setSubscriptionInterval] = useState<SubscriptionInterval>('P1W');
  const [buttonTextId, setButtonTextId] = useState<string>('1');
  const [stylesheetUrl, setStylesheetUrl] = useState('');
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  // Spec 514: Asset-Referenzen pro Slot ('' = kein Asset, Inline-URL gilt).
  const [stylesheetAssetId, setStylesheetAssetId] = useState('');
  const [headerAssetId, setHeaderAssetId] = useState('');
  const [backgroundAssetId, setBackgroundAssetId] = useState('');
  const [benefitText, setBenefitText] = useState('');
  const [carrierLogoId, setCarrierLogoId] = useState<string>('');
  const [themeLayout, setThemeLayout] = useState<string>('default'); // Spec 0087
  // Legal Links state (Spec 0040 M-6)
  const [agbUrl, setAgbUrl] = useState('');
  const [withdrawalUrl, setWithdrawalUrl] = useState('');
  const [imprintUrl, setImprintUrl] = useState('');
  const [privacyUrl, setPrivacyUrl] = useState('');
  const [supportUrl, setSupportUrl] = useState('');
  // Contract v2.0: Landing-Page ueberspringen
  const [skipLandingPage, setSkipLandingPage] = useState(false);
  // Spec 516: Journey-Host-Auswahl — `JOURNEY_HOST_DEFAULT` oder ein
  // https-Origin aus der Registry (bzw. der registry-fremde Bestandswert).
  const [journeyHostSelection, setJourneyHostSelection] = useState<string>(JOURNEY_HOST_DEFAULT);
  // Preview state
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('mobile');
  const [assetStatus, setAssetStatus] = useState<AssetValidationStatus>(EMPTY_ASSET_STATUS);

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Spec 516: Journey-Host — Feld-Gate (platform_admin-only, D-2), Stage +
  // geladener Wert (origin-normalisiert, Trap 6; unparsebarer Bestandswert
  // faellt auf den Rohwert zurueck, damit Erhalt/Anzeige nie verloren gehen).
  const { user } = useAuth();
  const canWriteJourneyHost =
    !!user && (ROLE_GROUPS.SERVICE_JOURNEY_HOST_WRITE as string[]).includes(user.role);
  const panelStage = resolvePayBaseUrl(window.location.hostname).stage;
  const loadedJourneyHost = service?.publicBaseUrl
    ? (normalizeOrigin(service.publicBaseUrl) ?? service.publicBaseUrl)
    : null;
  // Merchant-Kontext des Picker: Edit = Merchant des Service; Create = Auswahl
  // (Platform) bzw. eigener Tenant (Merchant-Rolle).
  const journeyHostMerchantId = isEditMode
    ? (service?.merchantId ?? '')
    : (showMerchant ? merchantId : (ownMerchantId ?? ''));
  const merchantAliasHosts = aliasHostsFor(panelStage, journeyHostMerchantId);
  // Registry-fremder Bestandswert (z. B. DDB-Direktwrite, Registry-Lag) bleibt
  // als markierte Option waehlbar/erhaltbar — stilles Ueberschreiben ausgeschlossen.
  const loadedHostOutsideRegistry =
    loadedJourneyHost !== null && !merchantAliasHosts.includes(loadedJourneyHost);

  // Reset form when service changes or dialog opens.
  // Spec 0216: Routing-Felder (fallbackChain, msisdnCollectionUrl) sind hier
  // NICHT mehr Teil des Form-States — sie werden in der eigenen Routing-View
  // gepflegt. Im Create-Mode erfasst der Operator den Primaer-Eintrag inline.
  useEffect(() => {
    if (open) {
      if (service) {
        setMerchantId(service.merchantId);
        setDescription(service.description);
        setDisplayName(service.displayName ?? '');
        setGrossAmount(String(service.grossAmount));
        setSubscriptionInterval(service.subscriptionInterval);
        // Issue #524: Theme-States aus der zentralen Normalform speisen — dieselbe Quelle
        // wie der Dirty-Vergleich am Submit, damit Reset und Vergleich nicht driften.
        const loadedTheme = loadedThemeFieldValues(service);
        setButtonTextId(loadedTheme.buttonTextId);
        setStylesheetUrl(loadedTheme.stylesheetUrl);
        setHeaderImageUrl(loadedTheme.headerImageUrl);
        setBackgroundImageUrl(loadedTheme.backgroundImageUrl);
        setStylesheetAssetId(loadedTheme.stylesheetAssetId);
        setHeaderAssetId(loadedTheme.headerAssetId);
        setBackgroundAssetId(loadedTheme.backgroundAssetId);
        setBenefitText(loadedTheme.benefitText);
        setCarrierLogoId(loadedTheme.carrierLogoId);
        setThemeLayout(service.themeLayout ?? 'default');
        setAgbUrl(service.legalLinks?.agbUrl ?? '');
        setWithdrawalUrl(service.legalLinks?.withdrawalUrl ?? '');
        setImprintUrl(service.legalLinks?.imprintUrl ?? '');
        setPrivacyUrl(service.legalLinks?.privacyUrl ?? '');
        setSupportUrl(service.legalLinks?.supportUrl ?? '');
        setSkipLandingPage(service.skipLandingPage ?? false);
        // Spec 516: Auswahl aus dem geladenen Wert (origin-normalisiert),
        // sonst Default. Unparsebarer Bestandswert -> Rohwert (Erhalt moeglich).
        setJourneyHostSelection(
          service.publicBaseUrl
            ? (normalizeOrigin(service.publicBaseUrl) ?? service.publicBaseUrl)
            : JOURNEY_HOST_DEFAULT
        );
        // Edit-Mode: Primaer-Routing-Inputs spielen keine Rolle.
        setPrimaryFlowType('');
        setPrimaryGatewayServiceId('');
        setPrimaryIqServiceId('');
      } else {
        // Reset to defaults for create mode
        setMerchantId('');
        setPrimaryFlowType('');
        setPrimaryGatewayServiceId('');
        setPrimaryIqServiceId('');
        setDescription('');
        setDisplayName('');
        setGrossAmount('');
        setSubscriptionInterval('P1W');
        setButtonTextId('1');
        setStylesheetUrl('');
        setHeaderImageUrl('');
        setBackgroundImageUrl('');
        setStylesheetAssetId('');
        setHeaderAssetId('');
        setBackgroundAssetId('');
        setBenefitText('');
        setCarrierLogoId('');
        setThemeLayout('default');
        setAgbUrl('');
        setWithdrawalUrl('');
        setImprintUrl('');
        setPrivacyUrl('');
        setSupportUrl('');
        setSkipLandingPage(false);
        setJourneyHostSelection(JOURNEY_HOST_DEFAULT);
      }
      setErrors({});
    }
  }, [open, service]);

  // Spec 516 Scope 4: Merchant-Wechsel im Create-Form setzt die Auswahl zurueck,
  // wenn der gewaehlte Host dem neuen Merchant nicht provisioniert ist — ein
  // Alias von Merchant A darf nach Umschalten auf B nie selektiert bleiben.
  useEffect(() => {
    if (isEditMode) {
      return;
    }
    setJourneyHostSelection((current) =>
      current !== JOURNEY_HOST_DEFAULT &&
      !aliasHostsFor(panelStage, journeyHostMerchantId).includes(current)
        ? JOURNEY_HOST_DEFAULT
        : current
    );
  }, [isEditMode, journeyHostMerchantId, panelStage]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!isEditMode) {
      // Spec 0220: nur Platform-Rollen geben den Merchant ein. Merchant-Rollen
      // erben ownMerchantId; ihr Feld entfaellt, daher hier nicht validieren.
      if (showMerchant) {
        if (!merchantId.trim()) {
          newErrors.merchantId = t('form.validation.merchantIdRequired');
        } else if (!/^[a-z0-9][a-z0-9-]{2,48}[a-z0-9]$/.test(merchantId)) {
          newErrors.merchantId = t('form.validation.merchantIdFormat');
        }
      }

      // Spec 0216 §5.2: Primaer-Routing ist im Create-Mode Pflicht (1 Entry).
      if (!primaryFlowType) {
        newErrors.primaryFlowType = t('form.validation.flowTypeRequired');
      }
      if (!primaryGatewayServiceId.trim()) {
        newErrors.primaryGatewayServiceId = t('form.validation.gatewayServiceIdRequired');
      } else if (!IQ_SERVICE_KEY_PATTERN.test(primaryGatewayServiceId)) {
        // Spec 344 UC-5: relaxed to the opaque inventory-key rule (only structural
        // breakers excluded). Validated UNTRIMMED so leading/trailing whitespace is
        // rejected (matches the value submitted at :369 + the routing-dialog behavior).
        newErrors.primaryGatewayServiceId = t('form.validation.gatewayServiceIdFormat');
      }

      // Issue #230: iqServiceId ist optional, muss aber bei Eingabe iq_… sein.
      if (primaryIqServiceId.trim() && !IQ_SERVICE_ID_REGEX.test(primaryIqServiceId.trim())) {
        newErrors.primaryIqServiceId = t('form.validation.iqServiceIdFormat');
      }
    }

    if (!description.trim()) {
      newErrors.description = t('form.validation.descriptionRequired');
    } else if (description.length > 32) {
      newErrors.description = t('form.validation.descriptionMaxLength');
    }

    // Spec 514: displayName optional, max 200.
    if (displayName.length > 200) {
      newErrors.displayName = t('form.validation.displayNameMaxLength');
    }

    const amount = parseInt(grossAmount, 10);
    if (isNaN(amount) || amount < 1) {
      newErrors.grossAmount = t('form.validation.grossAmountPositive');
    }

    // Validate URLs if provided
    const urlPattern = /^https:\/\/.+/;
    if (stylesheetUrl && !urlPattern.test(stylesheetUrl)) {
      newErrors.stylesheetUrl = t('form.validation.urlHttpsRequired');
    }
    if (headerImageUrl && !urlPattern.test(headerImageUrl)) {
      newErrors.headerImageUrl = t('form.validation.urlHttpsRequired');
    }
    if (backgroundImageUrl && !urlPattern.test(backgroundImageUrl)) {
      newErrors.backgroundImageUrl = t('form.validation.urlHttpsRequired');
    }

    // LegalLinks URL validation (Spec 0040)
    if (agbUrl && !urlPattern.test(agbUrl)) newErrors.agbUrl = t('form.validation.urlHttpsRequired');
    if (withdrawalUrl && !urlPattern.test(withdrawalUrl)) newErrors.withdrawalUrl = t('form.validation.urlHttpsRequired');
    if (imprintUrl && !urlPattern.test(imprintUrl)) newErrors.imprintUrl = t('form.validation.urlHttpsRequired');
    if (privacyUrl && !urlPattern.test(privacyUrl)) newErrors.privacyUrl = t('form.validation.urlHttpsRequired');
    if (supportUrl && !urlPattern.test(supportUrl)) newErrors.supportUrl = t('form.validation.urlHttpsRequired');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Spec 0220 Fail-safe: Merchant-Rolle ohne Tenant-Bindung → kein Create.
    if (merchantContextMissing) return;
    if (!validate()) return;

    // Create-Pfad: Theme nur senden, wenn ueberhaupt etwas gestaltet wurde (unveraendert).
    const hasThemeCustomization =
      buttonTextId !== '1' || stylesheetUrl || headerImageUrl || backgroundImageUrl ||
      stylesheetAssetId || headerAssetId || backgroundAssetId ||
      benefitText || (carrierLogoId && carrierLogoId !== 'none');

    // Issue #524: Edit-Pfad — Theme nur senden, wenn sich mindestens ein Theme-Feld gegenueber
    // dem geladenen Stand geaendert hat. Sonst `Theme` ganz weglassen, damit die Facade das
    // bestehende Theme (inkl. formular-unbekannter Felder wie `styleVariables`) nicht voll
    // ersetzt. Ein bewusst geleerter Asset-Slot ist eine Aenderung -> dirty; das geleerte Feld
    // reist dann als '' mit (assetIdFields sendet im Edit-Mode alle drei explizit). Damit ist
    // der fruehere `assetIdCleared`-Sonderfall subsumiert.
    const themeDirty =
      isEditMode &&
      isThemeDirty(loadedThemeFieldValues(service), {
        buttonTextId,
        stylesheetUrl,
        headerImageUrl,
        backgroundImageUrl,
        stylesheetAssetId,
        headerAssetId,
        backgroundAssetId,
        benefitText,
        carrierLogoId,
      });

    // Asset references: edit mode sends them explicitly (incl. '' = clear); create
    // mode only sends the non-empty ones.
    const assetIdFields = isEditMode
      ? { stylesheetAssetId, headerAssetId, backgroundAssetId }
      : {
          ...(stylesheetAssetId && { stylesheetAssetId }),
          ...(headerAssetId && { headerAssetId }),
          ...(backgroundAssetId && { backgroundAssetId }),
        };

    // Edit: Dirty-Guard; Create: bisheriges hasThemeCustomization-Gate.
    const shouldBuildTheme = isEditMode ? themeDirty : hasThemeCustomization;
    const theme = shouldBuildTheme
      ? {
          buttonTextId: parseInt(buttonTextId, 10) as 1 | 2 | 3 | 4,
          ...(stylesheetUrl && { stylesheetUrl }),
          ...(headerImageUrl && { headerImageUrl }),
          ...(backgroundImageUrl && { backgroundImageUrl }),
          // Spec 514: Asset-Referenzen parallel zu den Inline-URLs.
          ...assetIdFields,
          ...(benefitText && { benefitText }),
          ...(carrierLogoId && carrierLogoId !== 'none' && { complianceCarrierLogoId: Number(carrierLogoId) }),
        }
      : undefined;

    // Spec 514: displayName top-level, getrennt von description; leer = nicht senden.
    const trimmedDisplayName = displayName.trim();

    // LegalLinks (Spec 0040 M-6) — nur senden wenn min. 1 URL gesetzt
    const hasLegalLinks = agbUrl || withdrawalUrl || imprintUrl || privacyUrl || supportUrl;
    const legalLinks = hasLegalLinks
      ? {
          ...(agbUrl && { agbUrl }),
          ...(withdrawalUrl && { withdrawalUrl }),
          ...(imprintUrl && { imprintUrl }),
          ...(privacyUrl && { privacyUrl }),
          ...(supportUrl && { supportUrl }),
        }
      : undefined;

    // Spec 516: EIN Submit-Helfer fuer beide Pfade — Key nur bei echter
    // Aenderung durch einen Schreibberechtigten (Trap 2: PM sendet den Key NIE).
    const journeyHost = journeyHostSubmitValue(
      loadedJourneyHost,
      journeyHostSelection,
      canWriteJourneyHost
    );

    if (isEditMode && service) {
      // Spec 0216 / Conformity-Pflicht 4 (Plan §4.1): KRITISCH.
      // Routing-Felder (`fallbackChain`, `msisdnCollectionUrl`) duerfen im
      // Edit-Mode NIEMALS Teil des Update-Bodies sein. Partial-Update auf
      // Facade-Seite wuerde sonst die Live-Chain mit leerem/altem Wert
      // ueberschreiben. Routing wird ausschliesslich in der eigenen
      // Routing-View aktualisiert. Hier deshalb explizit nur Stammdaten.
      const data: UpdateServiceRequest = {
        description,
        // Spec 514: immer mitsenden, damit Leeren das Label löscht (empty erlaubt).
        displayName: trimmedDisplayName,
        grossAmount: parseInt(grossAmount, 10),
        subscriptionInterval,
        themeLayout: themeLayout as ThemeLayout,
        skipLandingPage,
        ...(theme && { theme }),
        ...(legalLinks && { legalLinks }),
        // Spec 516: null = entfernen (Fallback api.<stage>), Key-Omission = unveraendert.
        ...(journeyHost.include && { publicBaseUrl: journeyHost.value }),
      };
      onSubmitUpdate?.(service.serviceId, data);
    } else {
      // Spec 0216 §5.2: Create sendet eine 1-Entry-FallbackChain — der
      // Operator vervollstaendigt die Kette danach in der Routing-View.
      const primaryEntry: FallbackChainEntry = {
        flowType: primaryFlowType as FlowType,
        gatewayServiceId: primaryGatewayServiceId,
        ...(primaryIqServiceId.trim() ? { iqServiceId: primaryIqServiceId.trim() } : {}),
      };
      const data: CreateServiceRequest = {
        // Spec 0220: Platform sendet das gewaehlte Feld, Merchant-Rolle den
        // eigenen Tenant (ownMerchantId ist hier durch den Fail-safe non-null).
        merchantId: showMerchant ? merchantId : (ownMerchantId ?? ''),
        description,
        ...(trimmedDisplayName && { displayName: trimmedDisplayName }),
        grossAmount: parseInt(grossAmount, 10),
        currency: 'EUROCENT',
        subscriptionInterval,
        themeLayout: themeLayout as ThemeLayout,
        skipLandingPage,
        fallbackChain: [primaryEntry],
        ...(theme && { theme }),
        ...(legalLinks && { legalLinks }),
        // Spec 516: Create kennt kein Entfernen — nur ein gewaehlter Alias-Host
        // wird gesendet (Default = Key-Omission).
        ...(journeyHost.include && journeyHost.value !== null && { publicBaseUrl: journeyHost.value }),
      };
      onSubmitCreate?.(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[1152px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader className="pb-2">
            <DialogTitle className="text-xl">
              {isEditMode ? t('form.title.edit') : t('form.title.create')}
            </DialogTitle>
            <DialogDescription>
              {isEditMode
                ? t('form.description.edit')
                : t('form.description.create')}
            </DialogDescription>
          </DialogHeader>

          <Separator className="mb-6" />

          {/* Split Layout: Form + Preview */}
          <div className="grid gap-8 md:grid-cols-[1fr,auto]">
            {/* Left: Form Fields */}
            <div className="space-y-6">
              {/* Section: Identity */}
              {/* Spec 0128: Warnhinweis bei aktiven Services */}
              {isEditMode && service?.status === 'active' && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/20 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{t('form.activeWarning')}</span>
                </div>
              )}

              {/* Section: Identity (Create-Mode: merchantId) */}
              {/* Spec 0220: Merchant-Auswahl nur fuer Platform-Rollen. */}
              {!isEditMode && showMerchant && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('form.section.identity')}
                  </h4>
                  <div className="space-y-2">
                    <Label htmlFor="merchantId">
                      {t('form.label.merchantId')} <FieldHelpPopover helpKey="merchantId" /> <span className="text-destructive">*</span>
                    </Label>
                    <Combobox
                      id="merchantId"
                      options={merchantOptions}
                      value={merchantId}
                      onChange={setMerchantId}
                      placeholder={t('form.placeholder.merchantId')}
                      searchPlaceholder={t('form.merchantSearchPlaceholder')}
                      emptyText={t('form.merchantEmpty')}
                      allowFreeText
                      loading={merchantsLoading}
                      error={!!errors.merchantId}
                    />
                    {errors.merchantId && (
                      <p id="merchantId-error" className="text-[13px] text-destructive">
                        {errors.merchantId}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Spec 0216 §5.5: Edit-Mode hat KEIN Routing-Feld mehr — */}
              {/* Routing wird ausschliesslich ueber /services/:id/routing gepflegt. */}

              {/* Spec 0216 §5.2: Primaer-Routing inline im Create-Mode. */}
              {!isEditMode && (
                <div className="space-y-4" data-testid="primary-routing-section">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('form.section.primaryRouting')}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {t('form.help.primaryRouting')}
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="primaryFlowType">
                        {t('form.label.flowType')} <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={primaryFlowType}
                        onValueChange={(v) => setPrimaryFlowType(v as FlowType)}
                      >
                        <SelectTrigger
                          id="primaryFlowType"
                          aria-invalid={!!errors.primaryFlowType}
                          className={errors.primaryFlowType ? 'border-destructive' : ''}
                        >
                          <SelectValue placeholder={t('form.placeholder.flowType')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="WAP_CLICK">WAP_CLICK</SelectItem>
                          <SelectItem value="WEB_TAN">WEB_TAN</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.primaryFlowType && (
                        <p className="text-[13px] text-destructive">{errors.primaryFlowType}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="primaryGatewayServiceId">
                        {t('form.label.gatewayServiceId')} <span className="text-destructive">*</span>
                      </Label>
                      {/* UC-5 (Spec 344): InternetQ-Service-Kennung aus dem Merchant- */}
                      {/* Inventar (active, flowType-gefiltert); Freitext als Override. */}
                      <Combobox
                        id="primaryGatewayServiceId"
                        options={primaryGatewayOptions}
                        value={primaryGatewayServiceId}
                        onChange={setPrimaryGatewayServiceId}
                        placeholder={t('form.placeholder.gatewayServiceId')}
                        searchPlaceholder={t('form.placeholder.gatewayServiceId')}
                        emptyText={t('form.empty.gatewayFreeText')}
                        allowFreeText
                        wrapOptionLabel
                        loading={iqInventory.isLoading}
                        error={!!errors.primaryGatewayServiceId}
                      />
                      {errors.primaryGatewayServiceId && (
                        <p className="text-[13px] text-destructive">{errors.primaryGatewayServiceId}</p>
                      )}
                    </div>
                  </div>

                  {/* Issue #230: iqServiceId (Credential-Set) als separates, */}
                  {/* optionales Feld. Leer = Facade-Standard/Mock. */}
                  <div className="space-y-2">
                    <Label htmlFor="primaryIqServiceId">
                      {t('form.label.iqServiceId')}
                    </Label>
                    <Combobox
                      id="primaryIqServiceId"
                      options={iqServiceOptions}
                      value={primaryIqServiceId}
                      onChange={setPrimaryIqServiceId}
                      placeholder={t('form.placeholder.iqServiceId')}
                      searchPlaceholder={t('form.placeholder.iqServiceId')}
                      emptyText={t('form.empty.iqServices')}
                      allowFreeText
                      loading={iqServicesLoading}
                      error={!!errors.primaryIqServiceId}
                    />
                    {errors.primaryIqServiceId ? (
                      <p className="text-[13px] text-destructive">{errors.primaryIqServiceId}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">{t('form.help.iqServiceId')}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Section: Service Details */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('form.section.details')}
                </h4>

                {/* DisplayName (Spec 514 / Issue #338) — primaeres admin-seitiges Label,
                    klassische Reihenfolge Name → Description → Optionen. */}
                <div className="space-y-2">
                  <Label htmlFor="displayName">{t('form.label.displayName')}</Label>
                  <Input
                    id="displayName"
                    placeholder={t('form.placeholder.displayName')}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={200}
                    aria-invalid={!!errors.displayName}
                    aria-describedby={errors.displayName ? 'displayName-error' : undefined}
                    className={errors.displayName ? 'border-destructive focus-visible:ring-destructive' : ''}
                  />
                  {errors.displayName ? (
                    <p id="displayName-error" className="text-[13px] text-destructive">{errors.displayName}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t('form.help.displayName')}</p>
                  )}
                </div>

                {/* Description (kundenseitiger Produktname, Pflichtfeld) */}
                <div className="space-y-2">
                  <Label htmlFor="description">
                    {t('form.label.description')} <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder={t('form.placeholder.description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={32}
                    rows={3}
                    aria-invalid={!!errors.description}
                    aria-describedby={errors.description ? 'description-error' : undefined}
                    className={errors.description ? 'border-destructive focus-visible:ring-destructive' : ''}
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    {errors.description ? (
                      <p id="description-error" className="text-destructive">
                        {errors.description}
                      </p>
                    ) : (
                      <span />
                    )}
                    <span className={description.length > 28 ? 'text-amber-500 font-medium' : ''}>
                      {description.length}/32
                    </span>
                  </div>
                </div>

                {/* Amount and Interval */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="grossAmount">
                      {t('form.label.grossAmount')} <FieldHelpPopover helpKey="grossAmount" /> <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="grossAmount"
                      type="number"
                      min="1"
                      placeholder={t('form.placeholder.grossAmount')}
                      value={grossAmount}
                      onChange={(e) => setGrossAmount(e.target.value)}
                      aria-invalid={!!errors.grossAmount}
                      aria-describedby={errors.grossAmount ? 'grossAmount-error' : undefined}
                      className={errors.grossAmount ? 'border-destructive focus-visible:ring-destructive' : ''}
                    />
                    {errors.grossAmount && (
                      <p id="grossAmount-error" className="text-[13px] text-destructive">
                        {errors.grossAmount}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subscriptionInterval">
                      {t('form.label.subscriptionInterval')} <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={subscriptionInterval}
                      onValueChange={(v) => setSubscriptionInterval(v as SubscriptionInterval)}
                    >
                      <SelectTrigger id="subscriptionInterval">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERVALS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`interval.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Landing-Page Steuerung (Contract v2.0) — Option unterhalb Name/Description (Issue #338) */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('form.landingPageSection')}
                </Label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skipLandingPage}
                    onChange={(e) => setSkipLandingPage(e.target.checked)}
                    disabled={isSubmitting}
                    className="accent-primary h-4 w-4"
                  />
                  <div>
                    <span className="text-sm font-medium">{t('form.skipLandingPage')}</span>
                    <p className="text-xs text-muted-foreground">
                      {t('form.skipLandingPageHint')}
                    </p>
                  </div>
                </label>
              </div>

              {/* Spec 516 / Contract v2.20: Journey-Host (publicBaseUrl).
                  Picker (KEIN Freitext) nur fuer platform_admin; alle anderen
                  Form-Nutzer sehen den Wert read-only und senden den Key nie. */}
              <div className="space-y-2" data-testid="journey-host-section">
                <Label htmlFor="journeyHost">{t('form.journeyHost.label')}</Label>
                {canWriteJourneyHost ? (
                  <>
                    <Select
                      value={journeyHostSelection}
                      onValueChange={setJourneyHostSelection}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger id="journeyHost">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={JOURNEY_HOST_DEFAULT}>
                          {t('form.journeyHost.defaultOption', {
                            host: defaultJourneyHost(panelStage),
                          })}
                        </SelectItem>
                        {merchantAliasHosts.map((host) => (
                          <SelectItem key={host} value={host}>
                            <span className="font-mono text-sm">{host}</span>
                          </SelectItem>
                        ))}
                        {loadedHostOutsideRegistry && loadedJourneyHost && (
                          <SelectItem value={loadedJourneyHost}>
                            <span className="font-mono text-sm">{loadedJourneyHost}</span>{' '}
                            <span className="text-xs text-amber-600 dark:text-amber-400">
                              ({t('form.journeyHost.notInRegistry')})
                            </span>
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('form.journeyHost.help')}
                    </p>
                  </>
                ) : (
                  <p className="text-sm" data-testid="journey-host-readonly">
                    {loadedJourneyHost ? (
                      <span className="font-mono">{loadedJourneyHost}</span>
                    ) : (
                      t('form.journeyHost.defaultOption', {
                        host: defaultJourneyHost(panelStage),
                      })
                    )}
                  </p>
                )}
              </div>

              {/* Section: Theme */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('form.section.theme')}
                  </h4>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {t('common:label.optional')}
                  </span>
                </div>

                <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="buttonTextId">{t('form.label.buttonTextId')}</Label>
                    <Select value={buttonTextId} onValueChange={setButtonTextId}>
                      <SelectTrigger id="buttonTextId">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUTTON_TEXT_IDS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`buttonText.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* ThemeLayout (Spec 0087) */}
                  <div className="space-y-2">
                    <Label htmlFor="themeLayout" className="text-sm">
                      {t('form.label.themeLayout')}
                    </Label>
                    <Select value={themeLayout} onValueChange={setThemeLayout}>
                      <SelectTrigger id="themeLayout">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">{t('form.option.themeLayout.default')}</SelectItem>
                        <SelectItem value="gameloft">{t('form.option.themeLayout.gameloft')}</SelectItem>
                        <SelectItem value="minimal">{t('form.option.themeLayout.minimal')}</SelectItem>
                        <SelectItem value="two-zone-v2">{t('form.option.themeLayout.two-zone-v2')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('form.help.themeLayout')}
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <p className="text-xs font-medium text-muted-foreground">{t('form.assetUrls')}</p>

                    <div className="space-y-2">
                      <AssetSlotField
                        id="stylesheetAssetId"
                        slotType="stylesheet"
                        merchantId={merchantId || (ownMerchantId ?? '')}
                        value={stylesheetAssetId}
                        onChange={setStylesheetAssetId}
                        currentServiceId={service?.serviceId}
                      />
                      <Label htmlFor="stylesheetUrl" className="text-sm">
                        {t('form.label.stylesheetUrl')} <FieldHelpPopover helpKey="stylesheetUrl" />
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="stylesheetUrl"
                          type="url"
                          placeholder="https://example.com/style.css"
                          value={stylesheetUrl}
                          onChange={(e) => setStylesheetUrl(e.target.value)}
                          className={`flex-1 ${errors.stylesheetUrl ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                          aria-invalid={!!errors.stylesheetUrl}
                          aria-describedby={errors.stylesheetUrl ? 'stylesheetUrl-error' : undefined}
                        />
                        <AssetStatusIcon status={assetStatus.stylesheet} />
                      </div>
                      {errors.stylesheetUrl && (
                        <p id="stylesheetUrl-error" className="text-[13px] text-destructive">
                          {errors.stylesheetUrl}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <AssetSlotField
                        id="headerAssetId"
                        slotType="header"
                        merchantId={merchantId || (ownMerchantId ?? '')}
                        value={headerAssetId}
                        onChange={setHeaderAssetId}
                        currentServiceId={service?.serviceId}
                      />
                      <Label htmlFor="headerImageUrl" className="text-sm">
                        {t('form.label.headerImageUrl')} <FieldHelpPopover helpKey="headerImageUrl" />
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="headerImageUrl"
                          type="url"
                          placeholder="https://example.com/logo.png"
                          value={headerImageUrl}
                          onChange={(e) => setHeaderImageUrl(e.target.value)}
                          className={`flex-1 ${errors.headerImageUrl ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                          aria-invalid={!!errors.headerImageUrl}
                          aria-describedby={errors.headerImageUrl ? 'headerImageUrl-error' : undefined}
                        />
                        <AssetStatusIcon status={assetStatus.headerImage} />
                      </div>
                      {errors.headerImageUrl && (
                        <p id="headerImageUrl-error" className="text-[13px] text-destructive">
                          {errors.headerImageUrl}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <AssetSlotField
                        id="backgroundAssetId"
                        slotType="background"
                        merchantId={merchantId || (ownMerchantId ?? '')}
                        value={backgroundAssetId}
                        onChange={setBackgroundAssetId}
                        currentServiceId={service?.serviceId}
                      />
                      <Label htmlFor="backgroundImageUrl" className="text-sm">
                        {t('form.label.backgroundImageUrl')} <FieldHelpPopover helpKey="backgroundImageUrl" />
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="backgroundImageUrl"
                          type="url"
                          placeholder="https://example.com/background.jpg"
                          value={backgroundImageUrl}
                          onChange={(e) => setBackgroundImageUrl(e.target.value)}
                          className={`flex-1 ${errors.backgroundImageUrl ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                          aria-invalid={!!errors.backgroundImageUrl}
                          aria-describedby={
                            errors.backgroundImageUrl ? 'backgroundImageUrl-error' : undefined
                          }
                        />
                        <AssetStatusIcon status={assetStatus.backgroundImage} />
                      </div>
                      {errors.backgroundImageUrl && (
                        <p id="backgroundImageUrl-error" className="text-[13px] text-destructive">
                          {errors.backgroundImageUrl}
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* BenefitText (Spec 0039 M-5) */}
                  <div className="space-y-2">
                    <Label htmlFor="benefitText" className="text-sm">
                      {t('form.label.benefitText')}
                    </Label>
                    <Textarea
                      id="benefitText"
                      placeholder={t('form.placeholder.benefitText')}
                      value={benefitText}
                      onChange={(e) => {
                        if (e.target.value.length <= 200) setBenefitText(e.target.value);
                      }}
                      rows={2}
                      className={errors.benefitText ? 'border-destructive focus-visible:ring-destructive' : ''}
                      aria-invalid={!!errors.benefitText}
                      aria-describedby="benefitText-help"
                    />
                    <div className="flex items-center justify-between">
                      <p id="benefitText-help" className="text-xs text-muted-foreground">
                        {t('form.help.benefitText')}
                      </p>
                      <span className={`text-xs tabular-nums ${benefitText.length >= 200 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                        {benefitText.length}/200
                      </span>
                    </div>
                  </div>

                  {/* ComplianceCarrierLogoId (Spec 0039 M-5) */}
                  <div className="space-y-2">
                    <Label htmlFor="carrierLogoId" className="text-sm">
                      {t('form.label.carrierLogo')}
                    </Label>
                    <Select value={carrierLogoId || 'none'} onValueChange={setCarrierLogoId}>
                      <SelectTrigger id="carrierLogoId">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {CARRIER_LOGO_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={String(opt.id)}>
                            {opt.labelKey ? t(opt.labelKey) : opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('form.help.carrierLogo')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section: Legal Links (Spec 0040 M-6) */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('legalLinks.title')}
                  </h4>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {t('common:label.optional')}
                  </span>
                </div>

                <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                  <p className="text-xs text-muted-foreground">{t('legalLinks.formHint')}</p>

                  {([
                    { id: 'agbUrl', value: agbUrl, setter: setAgbUrl },
                    { id: 'withdrawalUrl', value: withdrawalUrl, setter: setWithdrawalUrl },
                    { id: 'imprintUrl', value: imprintUrl, setter: setImprintUrl },
                    { id: 'privacyUrl', value: privacyUrl, setter: setPrivacyUrl },
                    { id: 'supportUrl', value: supportUrl, setter: setSupportUrl },
                  ] as const).map(({ id, value, setter }) => (
                    <div key={id} className="space-y-1">
                      <Label htmlFor={id} className="text-sm">{t(`legalLinks.${id}`)}</Label>
                      <Input
                        id={id}
                        type="url"
                        placeholder="https://..."
                        value={value}
                        onChange={(e) => setter(e.target.value)}
                        className={errors[id] ? 'border-destructive focus-visible:ring-destructive' : ''}
                        aria-invalid={!!errors[id]}
                        aria-describedby={errors[id] ? `${id}-error` : undefined}
                      />
                      {errors[id] && (
                        <p id={`${id}-error`} className="text-[13px] text-destructive">{errors[id]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Preview */}
            <div className="hidden md:block">
              <div className="sticky top-0">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('preview')}
                  </span>
                  <PreviewDeviceToggle mode={deviceMode} onChange={setDeviceMode} />
                </div>
                <div className="rounded-lg border bg-muted/20 p-3">
                  <PaymentPagePreview
                    description={description}
                    grossAmount={parseInt(grossAmount, 10) || 0}
                    subscriptionInterval={subscriptionInterval}
                    theme={{
                      buttonTextId: parseInt(buttonTextId, 10) as 1 | 2 | 3 | 4,
                      stylesheetUrl: stylesheetUrl || undefined,
                      headerImageUrl: headerImageUrl || undefined,
                      backgroundImageUrl: backgroundImageUrl || undefined,
                    }}
                    themeLayout={themeLayout as ThemeLayout}
                    deviceMode={deviceMode}
                    onAssetValidation={setAssetStatus}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator className="mt-6 mb-4" />

          {/* Spec 0220 Fail-safe: Merchant-Rolle ohne Tenant-Bindung. */}
          {merchantContextMissing && (
            <p role="alert" className="mb-3 text-[13px] text-destructive">
              {t('form.validation.merchantContextMissing')}
            </p>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common:button.cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting} disabled={merchantContextMissing}>
              {isEditMode ? t('common:button.save') : t('common:button.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
